import { SignupNewUserBody, SignupNewUserResponse } from '@bounty/common'
import { COUNTRIES } from '@bounty/constants'
import {
  Dialog,
  H3,
  Logo,
  Paragraph,
  XStack,
  YStack,
  CartIcon,
  DotsIcon,
  HandPointingIcon,
  ArrowsHorizontalIcon,
  useAnalytics,
} from '@bounty/creators-design-system'
import { ReactNode, useState } from 'react'
import { z } from 'zod'
import { useFastForm } from '../../../../components'
import { API_URL } from '../../../../config/env'
import axios from 'redaxios'
import { CreateUserErrorComponent } from '../../../../hooks/useAuth/AuthErrorHandlers'
import { logger } from '../../../../utils/logger'
import { useShopDetailContext } from '../ShopDetailContext'
import { ConsentText } from '../../../../components/ConsentText'
import { useStorefrontPress } from '../useStorefrontPress'
import { isNil } from '@bounty/utils'
import AsyncStorage from '@react-native-async-storage/async-storage'

const SignUpForm = ({
  eventParams,
  redirectToUrl,
}: {
  eventParams: Record<string, any>
  redirectToUrl: ReturnType<typeof useStorefrontPress>['redirectToUrl']
}) => {
  const { track } = useAnalytics()
  const [error, setError] = useState<ReactNode>(null)
  const { Form, Input, SubmitButton } = useFastForm({
    schema: z.object({
      phoneNumber: z.string().min(7),
    }),
  })

  return (
    <Form
      mb="$4"
      onSubmit={async ({ phoneNumber }) => {
        const phoneNumberCountry = COUNTRIES[0]

        const resp = await axios
          .post<SignupNewUserResponse>(`${API_URL}/signup/create-user`, {
            phone: `${COUNTRIES[0]['prefix']}${phoneNumber}`,
          } as SignupNewUserBody)
          .catch(async (e) => {
            if (isNil(e?.data?.code)) {
              logger.error(e)
              return
            }

            if (e.data.code === 'DUPLICATE_PHONE_NUMBER') {
              logger.log('User account already exists. Redirecting to shop!')
              redirectToUrl({
                isNewUser: false,
                pixelParams: { location: 'public' },
              })
              return
            }

            setError(
              <CreateUserErrorComponent
                phoneNumber={phoneNumber}
                phoneNumberCountry={phoneNumberCountry}
                response={e.data}
              />,
            )
          })

        if (resp && resp.data.success) {
          track('Storefront Sign Up Conversion', eventParams)
          await AsyncStorage.setItem('analyticsUserId', resp.data.userId)
          redirectToUrl({
            isNewUser: true,
            userId: resp.data.userId,
            pixelParams: { location: 'public' },
          })
        }
      }}
    >
      <Input
        name="phoneNumber"
        keyboardType="phone-pad"
        textContentType="telephoneNumber"
        placeholder="phone number"
      />
      <SubmitButton event="Storefront Sign Up Before Redirect Submitted">
        Go Shopping
      </SubmitButton>
      {error}
    </Form>
  )
}

export const SignUpModal = () => {
  const { redirectToUrl } = useStorefrontPress()
  const [{ eventParams, signUpScreenOpen }, setShopDetailContext] =
    useShopDetailContext()

  return (
    <Dialog
      open={signUpScreenOpen}
      onOpenChange={(openChangeValue) =>
        setShopDetailContext((x) => ({
          ...x,
          signUpScreenOpen: openChangeValue,
        }))
      }
    >
      <XStack mb="$6">
        <Logo size={100} />
      </XStack>
      <YStack>
        <H3 size="$xl" mb="$6">
          Sign up to post on TikTok and get paid.
        </H3>
        <YStack
          p="$5"
          backgroundColor={'rgba(92, 225, 230, 1)'}
          borderRadius="$5"
          mb="$6"
        >
          <XStack>
            <XStack position="relative" pb="$6" mr="$3">
              <DotsIcon size={24} color="$neutral.50" />
              <XStack position="absolute" left={12} top={8}>
                <HandPointingIcon size={22} color="$neutral.50" />
              </XStack>
            </XStack>
            <XStack mr="$1">
              <ArrowsHorizontalIcon color="$neutral.50" />
            </XStack>
            <CartIcon color="$neutral.50" weight="fill" />
          </XStack>
          <Paragraph color="$neutral.50" size="$sm">
            Be sure to use the same phone number at checkout that you use to
            sign up for Bounty so we can match your order.
          </Paragraph>
        </YStack>
        <SignUpForm
          eventParams={eventParams}
          redirectToUrl={async (props) => {
            await redirectToUrl(props)
            setShopDetailContext((x) => ({ ...x, signUpScreenOpen: false }))
          }}
        />
        <ConsentText
          alignSelf="flex-end"
          color="$neutral.500"
          lineHeight={12}
        />
      </YStack>
    </Dialog>
  )
}
